import { every, find, some } from 'lodash'
import { isDependentFieldHidden } from '../../../common/validation/functions'
import formatFieldOutput from './formatFieldOutput'

/**
 * This function generates the strengths and needs for the current initial
 * assessment's equivalent care plan.
 *
 * @param {Object} formData the key-value pairs containing the information that
 * the user has entered in the initial assessment
 * @param {Array} formFields the fields generated by the user in the FormBuilder
 * @param {Object} resident the current resident
 */
export default function getStrengthsAndNeeds({
  formData,
  formFields,
  resident,
}) {
  let result = {
    strengths: '',
    needs: '',
    baselineBehaviour: '',
    behaviourEscalation: '',
    crisisPoint: '',
  }

  for (let field of formFields) {
    const output = getFieldOutput({ field, formData, resident })
    result.strengths += output.strengths
    result.needs += output.needs
    result.baselineBehaviour += output.baselineBehaviour
    result.behaviourEscalation += output.behaviourEscalation
    result.crisisPoint += output.crisisPoint
  }

  const hasBaselineBehaviour = result?.baselineBehaviour !== ''
  const hasBehaviourEscalation = result?.behaviourEscalation !== ''
  const hasCrisisPoint = result?.crisisPoint !== ''

  const finalResult = { ...result }

  if (!hasBaselineBehaviour) {
    delete finalResult.baselineBehaviour
  }

  if (!hasBehaviourEscalation) {
    delete finalResult.behaviourEscalation
  }

  if (!hasCrisisPoint) {
    delete finalResult.crisisPoint
  }

  return finalResult
}

/**
 * This function does the most heavy lifting for the initial assessment text
 * generation.
 *
 * @param {Object} field the current FormBuilder-generated field. Includes
 * options such as output type and dependent fields.
 * @param {Object} formData the fields generated by the user in the FormBuilder
 * @param {Object} resident the current resident
 */
function getFieldOutput({ field, formData, resident }) {
  let result = {
    strengths: '',
    needs: '',
    baselineBehaviour: '',
    behaviourEscalation: '',
    crisisPoint: '',
  }

  const value = formData[field.key]
  const comment = getComment(field, formData)
  const hasDependentFields =
    field.dependentFields && field.dependentFields.length

  const shouldHideFieldOutput = !shouldGetOutputForField({
    formData,
    field,
    value,
  })

  if (hasDependentFields && shouldHideFieldOutput) {
    /**
     * If the main field should hide its output, also check that each of the
     * dependent fields should also hide their output
     */
    const shouldHideDependentFieldOutput = every(
      field.dependentFields,
      (dependentField) =>
        !shouldGetOutputForField({
          formData,
          field: dependentField,
          value: formData[dependentField.key],
        })
    )
    if (shouldHideDependentFieldOutput) {
      return result
    }
  } else if (shouldHideFieldOutput) {
    return result
  }

  const { type, answer } = getOutputTypeAndAnswer(field, value)

  // Generate the text inside `formatFieldOutput` function and add to result
  result[type] += formatFieldOutput({ answer, comment, field, resident })

  if (field.dependentFields && field.dependentFields.length) {
    for (let dependentField of field.dependentFields) {
      // Don't do anything for dependent fields that are hidden
      if (isDependentFieldHidden(formData, dependentField.dependsOn)) {
        continue
      }

      // Run the function recursively for dependent fields
      const output = getFieldOutput({
        field: dependentField,
        formData,
        resident,
      })
      result.strengths += output.strengths
      result.needs += output.needs
      result.baselineBehaviour += output.baselineBehaviour
      result.behaviourEscalation += output.behaviourEscalation
      result.crisisPoint += output.crisisPoint
    }
  }

  return result
}

const getComment = (field, formData) => {
  const commentKey = `${field.key}_comment`
  return field.hasComment && formData.hasOwnProperty(commentKey)
    ? formData[commentKey]
    : undefined
}

const getMultiValueInput = (options, values) => {
  return options.reduce((input, option) => {
    if (option.value && values.includes(option.value)) {
      input.push(option.output || option.label)
    }
    return input
  }, [])
}

const getOutputTypeAndAnswer = (field, value) => {
  const options = field.values
  if (options && value instanceof Array && field.outputType) {
    return {
      type: field.outputType,
      answer: getMultiValueInput(options, value),
    }
  }
  const option = find(options, { value })
  if (option) {
    return {
      type: option.outputType,
      answer: option.output || option.label,
    }
  } else {
    return {
      type: field && field.outputType,
      answer: value,
    }
  }
}

const hasValidOutputType = (object) =>
  object && object.outputType && object.outputType !== 'na'

/**
 * Checks if we should generate output for a particular field. Fields must have
 * a value in the form data, and must have an outputType that isn't 'na' (or
 * at least one of their options must).
 */
const shouldGetOutputForField = ({ formData, field, value }) =>
  formData.hasOwnProperty(field.key) &&
  value !== '' &&
  !(value instanceof Array && !value.length) &&
  (hasValidOutputType(field) ||
    (field.values && some(field.values, hasValidOutputType)))
